/* eslint-disable max-lines */
/* eslint-disable max-statements */
import { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { message } from 'antd';

import { companies } from 'config/companies';
import { MODE, SHIFT } from 'models/baselines';
import { DateTimeFormat } from 'config/const';
import {
  useUpdateBaselineApi,
  useAddBaselineApi
} from 'services/baselines.service';
import { BaselinesContext } from 'providers/BaselinesProvider';
import { useAssociateCompanyToWorkCenterApi } from 'services/workcenters.service';
import { getBaselineTotal, getCompanyNameFromId } from '../Baselines.helpers';
import { BaselineItemDeps } from '../Baselines.types';
import {
  checkRequestedDatesAreNotEmpty,
  checkRequestedAreNotEqualToApproved,
  getAssociatedCompanies,
  getCompanyDropdownList,
  getTimePeriod,
  getTimePeriodTotals,
  getWorkCenterById,
  updateIsExpanded
} from './BaselineItem.helpers';

const useBaselineItem = ({
  id,
  workCenter,
  approved,
  requested,
  onIconClickHandler
}: BaselineItemDeps) => {
  const { getBaselines, workCenters, getWorkCenters } =
    useContext(BaselinesContext);
  const [updateBaselineState, updateBaseline] = useUpdateBaselineApi();
  const [addBaselineState, addBaseline] = useAddBaselineApi();
  const [associateCompanyState, associateCompany] =
    useAssociateCompanyToWorkCenterApi();
  const {
    error: updateBaselineError,
    responseStatus: updateBaselineResponseStatus,
    loading: updateBaselineLoading
  } = updateBaselineState.state;
  const {
    error: addBaselineError,
    responseStatus: addBaselineResponseStatus,
    loading: addBaselineLoading
  } = addBaselineState.state;
  const {
    error: associateCompanyError,
    loading: associateCompanyLoading,
    responseStatus: associateCompanyResponseStatus
  } = associateCompanyState.state;
  const [approvedState, setApprovedState] = useState(_.cloneDeep(approved));
  const [requestedState, setRequestedState] = useState(_.cloneDeep(requested));
  const [
    associateCompanyToWorkCenterPayload,
    setAssociateCompanyToWorkCenterPayload
  ] = useState<string[]>([]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(
    checkRequestedDatesAreNotEmpty(requestedState)
  );
  const [companyDropdownList, setCompanyDropdownList] = useState(
    getCompanyDropdownList(companies, workCenters, workCenter)
  );
  const [companyDropdownValue, setCompanyDropdownValue] =
    useState('Add company');

  let totalDay = 0;
  let totalNight = 0;
  const totalDayIncrement = (count: number) => {
    totalDay += count;
  };
  const totalNightIncrement = (count: number) => {
    totalNight += count;
  };

  approvedState.forEach((period) =>
    period.baselines.forEach((item) => {
      totalDayIncrement(item.day);
      totalNightIncrement(item.night);
    })
  );

  let hasRequested = false;
  if (requested && requested.length > 0) hasRequested = true;

  const [messageApi, contextHolder] = message.useMessage();
  useEffect(() => {
    if (!updateBaselineLoading && updateBaselineResponseStatus === 200) {
      messageApi.success(`Successfully updated Baselines for ${workCenter}`, 3);
      getBaselines();
    }
    if (updateBaselineError) {
      messageApi.error(`Error while updating Baselines for ${workCenter}`, 3);
    }
    if (!addBaselineLoading && addBaselineResponseStatus === 201) {
      messageApi.success(`Successfully added Baselines for ${workCenter}`, 3);
      getBaselines();
    }
    if (addBaselineError) {
      messageApi.error(`Error while adding Baselines for ${workCenter}`, 3);
    }
  }, [
    updateBaselineLoading,
    updateBaselineError,
    updateBaselineResponseStatus,
    addBaselineLoading,
    addBaselineError,
    addBaselineResponseStatus
  ]);

  // this useEffect checks if associating a company is successful, if it is
  // it then calls addBaseline (if there is no existing baseline) or updateBaseline (if there is already an existing one)
  useEffect(() => {
    if (!associateCompanyLoading && associateCompanyResponseStatus === 200) {
      messageApi.success(
        `Successfully associated companies for ${workCenter}`,
        3
      );
      setAssociateCompanyToWorkCenterPayload([]);
      getWorkCenters();
      const payload = requestedState.map((timePeriod) => {
        return _.omit(timePeriod, ['isExpanded', 'totalDay', 'totalNight']);
      });
      if (id === null) {
        addBaseline({
          addBaselinePayload: {
            workCenter,
            approved: [],
            requested: payload
          }
        });
      } else {
        updateBaseline({
          id: id as string,
          updateBaselinePayload: {
            requested: payload
          }
        });
      }
    }
    if (associateCompanyError) {
      messageApi.error(
        `Error while associating companies for ${workCenter}, updating baseline will not be processed`,
        5
      );
    }
  }, [
    associateCompanyLoading,
    associateCompanyResponseStatus,
    associateCompanyError
  ]);

  useEffect(() => {
    if (requested && requested.length) {
      setRequestedState(getTimePeriodTotals(_.cloneDeep(requested)));
    }
    if (approved && approved.length) {
      setApprovedState(getTimePeriodTotals(_.cloneDeep(approved)));
    }
  }, [requested, approved]);

  const addNewRange = () => {
    const associatedCompanies = getAssociatedCompanies(workCenters, workCenter);
    const baselinesFromAssociatedCompanies = associatedCompanies.map(
      (company) => {
        return {
          companyId: company,
          companyName: getCompanyNameFromId(company),
          day: 0,
          night: 0
        };
      }
    );
    setIsSubmitDisabled(true);

    if (requestedState === null || requestedState.length === 0) {
      setRequestedState([
        {
          from: moment().add(1, 'days').format(DateTimeFormat.Database),
          to: '',
          baselines: baselinesFromAssociatedCompanies
        }
      ]);
    } else {
      requestedState.push({
        from: moment().add(1, 'days').format(DateTimeFormat.Database),
        to: '',
        baselines: baselinesFromAssociatedCompanies
      });
      setRequestedState([...requestedState]);
    }
  };

  const onExpandRowHandler = (
    index: number,
    isExpanded: boolean,
    mode: MODE
  ) => {
    if (mode === MODE.EDIT) {
      updateIsExpanded(requestedState, index, isExpanded);
      setRequestedState([...requestedState]);
    }
    if (mode === MODE.VIEW) {
      updateIsExpanded(approvedState, index, isExpanded);
      setApprovedState([...approvedState]);
    }
  };

  const onSubmitClickHandler = () => {
    onIconClickHandler(MODE.NONE, workCenter);
    const payload = requestedState.map((timePeriod) => {
      return _.omit(timePeriod, ['isExpanded', 'totalDay', 'totalNight']);
    });

    const workCenterId = getWorkCenterById(workCenters, workCenter);
    if (associateCompanyToWorkCenterPayload.length > 0 && workCenterId) {
      associateCompany({
        associateCompanyToWorkCenterPayload,
        id: workCenterId
      });
    }
    if (associateCompanyToWorkCenterPayload.length === 0) {
      return id === null
        ? addBaseline({
            addBaselinePayload: {
              workCenter,
              approved: [],
              requested: payload
            }
          })
        : updateBaseline({
            id: id as string,
            updateBaselinePayload: {
              requested: payload
            }
          });
    }
    return true;
  };

  const onCancelClickHandler = () => {
    if (requested) {
      setRequestedState(_.cloneDeep(requested));
    } else {
      setRequestedState([]);
    }
    onIconClickHandler(MODE.NONE, workCenter);
    setIsSubmitDisabled(checkRequestedDatesAreNotEmpty(requested));
  };

  const insertBaselineToPeriod = (periodIndex: number, companyId: string) => {
    const periodToUpdate = getTimePeriod(requestedState, periodIndex);
    periodToUpdate.baselines.push({
      companyId,
      companyName: getCompanyNameFromId(companyId),
      day: 0,
      night: 0
    });
    setRequestedState([...requestedState]);
  };

  const onAddCompanySelectHandler = (
    periodIndex: number,
    companyId: string
  ) => {
    setAssociateCompanyToWorkCenterPayload([
      ...associateCompanyToWorkCenterPayload,
      companyId
    ]);
    const companyIndex = companyDropdownList.findIndex(
      (item) => item.value === companyId
    );
    companyDropdownList.splice(companyIndex, 1);
    setCompanyDropdownList([...companyDropdownList]);
    setCompanyDropdownValue('Add company');
    insertBaselineToPeriod(periodIndex, companyId);
    setIsSubmitDisabled(
      checkRequestedDatesAreNotEmpty(requestedState) ||
        checkRequestedAreNotEqualToApproved(requestedState, approvedState)
    );
  };

  const updateShift = (
    periodIndex: number,
    companyIndex: number,
    newValue: number,
    shift: SHIFT
  ) => {
    let updatedValue = newValue;
    if (updatedValue < 0) {
      updatedValue = 0;
    }
    const periodToUpdate = getTimePeriod(requestedState, periodIndex);

    if (shift === SHIFT.DAY && periodToUpdate) {
      _.set(periodToUpdate, `baselines[${companyIndex}].day`, updatedValue);
      _.set(
        periodToUpdate,
        'totalDay',
        getBaselineTotal(periodToUpdate.baselines, SHIFT.DAY)
      );
    } else if (shift === SHIFT.NIGHT && periodToUpdate) {
      _.set(periodToUpdate, `baselines[${companyIndex}].night`, updatedValue);
      _.set(
        periodToUpdate,
        'totalNight',
        getBaselineTotal(periodToUpdate.baselines, SHIFT.NIGHT)
      );
    }
    setIsSubmitDisabled(
      checkRequestedDatesAreNotEmpty(requestedState) ||
        checkRequestedAreNotEqualToApproved(requestedState, approvedState)
    );
    setRequestedState([...requestedState]);
  };

  const updateDate = (date: any, periodIndex: number, tofrom: string) => {
    const periodToUpdate = _.get(requestedState, periodIndex);

    if (tofrom === 'to' && periodToUpdate) {
      _.set(periodToUpdate, 'to', date.format('YYYY-MM-DD'));
    } else if (tofrom === 'from' && periodToUpdate) {
      _.set(periodToUpdate, 'from', date.format('YYYY-MM-DD'));
    }
    setIsSubmitDisabled(
      checkRequestedDatesAreNotEmpty(requestedState) ||
        checkRequestedAreNotEqualToApproved(requestedState, approvedState)
    );
    setRequestedState([...requestedState]);
  };

  return {
    approvedState,
    requestedState,
    companyDropdownList,
    contextHolder,
    companyDropdownValue,
    isSubmitDisabled,
    totalDay,
    totalNight,
    hasRequested,
    addNewRange,
    updateShift,
    updateDate,
    onExpandRowHandler,
    onSubmitClickHandler,
    onCancelClickHandler,
    onAddCompanySelectHandler
  };
};

export default useBaselineItem;
